import type { CSSProperties } from "react"
import { Check } from "lucide-react"
import { Button } from "../Button/Button"
import { cn } from "@/lib/utils"

/**
 * "consumed" and "expired" are derived server-side from the wallet's spend and
 * date; "cancelled" is a stored flag support can set. Only an active row still
 * has a balance.
 */
export type TopUpStatus = "active" | "consumed" | "expired" | "cancelled"

export interface TopUpLedgerRow {
  id: string
  /** ISO date or anything `new Date()` parses. */
  date: string
  /** Purchase amount in the account currency. */
  amount: number
  /** How much of this purchase is spent, 0-100. */
  pct: number
  status: TopUpStatus
  /** When active credit runs out. Ignored for the other statuses. */
  expiresAt?: string
}

export interface TopUpLedgerProps {
  /** Unspent credit across all rows. */
  balance: number
  rows: TopUpLedgerRow[]
  onAddMore?: () => void
  className?: string
}

function formatDate(input?: string) {
  if (!input) return ""
  const date = new Date(input.replace(" ", "T"))
  if (Number.isNaN(date.getTime())) return input
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function describe(row: TopUpLedgerRow) {
  switch (row.status) {
    case "consumed": return "Fully used"
    case "expired": return "Expired"
    case "cancelled": return "Cancelled"
    default: return row.expiresAt ? `Expires ${formatDate(row.expiresAt)}` : "Active"
  }
}

/**
 * Ledger of top-up purchases, one row per purchase with its own consumption
 * meter. Credit drains oldest-expiring first, so a row fills to 100% and flips
 * to a tick before the next one starts drawing down.
 *
 * Purely presentational — the caller owns allocation and ordering.
 */
export function TopUpLedger({ balance, rows, onAddMore, className }: TopUpLedgerProps) {
  if (rows.length === 0) return null

  return (
    <div
      className={cn(
        "rounded-md border border-border bg-card p-5 flex flex-col gap-3",
        className,
      )}
    >
      {/* The section heading above the card already names this, so the card
          leads with the number rather than repeating the title. */}
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-caption text-muted-foreground">Available credit</span>
        <span className="text-section text-foreground font-semibold tabular-nums shrink-0">
          ${balance.toFixed(2)}
        </span>
      </div>

      <div className="flex flex-col divide-y divide-border border-t border-border">
        {rows.map((row) => {
          const pct = Math.max(0, Math.min(100, row.pct))
          // Expired and cancelled credit is gone whatever the meter says, so
          // the whole row dims rather than advertising a balance that is not
          // spendable.
          const dead = row.status === "expired" || row.status === "cancelled"
          return (
            <div
              key={row.id}
              className={cn("flex items-center gap-3 py-2.5", dead && "opacity-60")}
            >
              <span className="text-caption text-muted-foreground tabular-nums w-24 shrink-0">
                {formatDate(row.date)}
              </span>
              <span className="text-caption text-foreground font-medium tabular-nums w-16 shrink-0">
                ${row.amount.toFixed(2)}
              </span>
              <span className="text-caption text-muted-foreground flex-1 min-w-0 truncate">
                {describe(row)}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full w-[var(--p)] motion-safe:transition-[width] motion-safe:duration-500",
                      dead ? "bg-muted-foreground/40" : "bg-primary",
                    )}
                    style={{ "--p": `${pct}%` } as CSSProperties}
                  />
                </div>
                <span className="w-8 shrink-0 flex justify-end">
                  {row.status === "consumed" ? (
                    <Check
                      size={14}
                      strokeWidth={2}
                      className="text-success"
                      aria-label="Fully consumed"
                    />
                  ) : (
                    <span className="text-caption text-muted-foreground tabular-nums">
                      {pct}%
                    </span>
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {onAddMore && (
        <div className="flex items-center justify-end border-t border-border pt-3">
          <Button
            size="sm"
            variant="outline"
            className="text-label shrink-0 motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100"
            onClick={onAddMore}
          >
            Add more
          </Button>
        </div>
      )}
    </div>
  )
}
